import { ProviderOptionsSchema } from '@promptfoo/validators/providers';
import type { ProviderOptions } from '@promptfoo/types';

import { z } from 'zod';

export enum ServerSettingsKeys {
  redteamProvider = 'redteamProvider',
  gradingProvider = 'gradingProvider',
  guardrailsProvider = 'guardrailsProvider',
  multilingualProvider = 'multilingualProvider',
  agentProvider = 'agentProvider',
  fusionAuthConfig = 'fusionAuthConfig',
  redteamGenerationProvider = 'redteamGenerationProvider',
  setupComplete = 'setupComplete',
  deploymentKeyData = 'deploymentKeyData',
}

export const ServerSettingSchema = z.object({
  key: z.nativeEnum(ServerSettingsKeys),
  value: z.any(),
});

export const FusionAuthConfigSchema = z.object({
  apiKey: z.string(),
});

// Structural type for Zod-like schemas. Using this instead of z.ZodType avoids
// cross-module type incompatibility when promptfoo is built with a different Zod version.
// This type represents what we actually need: a schema with a parse method.
interface ZodLikeSchema {
  parse: (data: unknown) => unknown;
}

// Apply strict mode to disallow extra properties in the provider options.
// The schema is Zod-compatible at runtime regardless of which Zod version built it.
const StrictProviderOptionsSchema = ProviderOptionsSchema.strict();

// Create a custom union validator that accepts either a string or ProviderOptions.
// We can't use z.union() because it requires both schemas to be from the same Zod instance,
// but StrictProviderOptionsSchema is from the promptfoo submodule (possibly different Zod types).
const stringOrProviderSchema: ZodLikeSchema = {
  parse: (data: unknown): unknown => {
    // If it's a string, validate with our Zod string schema
    if (typeof data === 'string') {
      return z.string().parse(data);
    }
    // Otherwise, validate with the promptfoo schema
    return StrictProviderOptionsSchema.parse(data);
  },
};

/** Reject Decisions for the shared grading slot, which also powers summaries and JSON generation. */
export function assertCompatibleGradingProvider(provider: string | ProviderOptions | null): void {
  const id = typeof provider === 'string' ? provider : provider?.id;
  if (id === 'openai:decisions' || id?.startsWith('openai:decisions:')) {
    throw new z.ZodError([
      {
        code: 'custom',
        path: [],
        message:
          'The shared grading provider must support text generation. Configure OpenAI Decisions on individual llm-rubric or classifier assertions instead.',
      },
    ]);
  }
}

const gradingProviderSchema: ZodLikeSchema = {
  parse: (data: unknown): unknown => {
    const provider = stringOrProviderSchema.parse(data);
    assertCompatibleGradingProvider(provider as string | ProviderOptions);
    return provider;
  },
};

export const ServerSettingsSchemas: Record<ServerSettingsKeys, ZodLikeSchema> = {
  [ServerSettingsKeys.redteamProvider]: stringOrProviderSchema,
  [ServerSettingsKeys.gradingProvider]: gradingProviderSchema,
  [ServerSettingsKeys.guardrailsProvider]: stringOrProviderSchema,
  [ServerSettingsKeys.multilingualProvider]: stringOrProviderSchema,
  [ServerSettingsKeys.agentProvider]: stringOrProviderSchema,
  [ServerSettingsKeys.redteamGenerationProvider]: stringOrProviderSchema,
  [ServerSettingsKeys.fusionAuthConfig]: z.object({
    apiKey: z.string().optional(),
  }),
  [ServerSettingsKeys.setupComplete]: z.object({
    completedAt: z.string(),
  }),
  [ServerSettingsKeys.deploymentKeyData]: z.object({
    licenseId: z.string(),
    customerName: z.string(),
    expirationDate: z.number(),
    domain: z.string(),
    fusionAuthEnabled: z.boolean(),
    numTargets: z.number().optional(),
    numProbes: z.number().optional(),
    contractStartDate: z.number().optional(),
    adminEmail: z.string().email().optional(),
    generatedAt: z.number().optional(),
    generatedBy: z.string().optional(),
    idp: z.unknown().optional(),
    appliedAt: z.string(),
  }),
};

export type ServerSettingDTO = z.infer<typeof ServerSettingSchema>;
export type FusionAuthConfig = z.infer<typeof FusionAuthConfigSchema>;
